import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Capacitor } from "@capacitor/core";
import { inject, Injectable, Injector } from "@angular/core";
import {
	Auth,
	authState,
	confirmPasswordReset,
	reauthenticateWithCredential,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
	verifyPasswordResetCode,
	User,
	GoogleAuthProvider,
	OAuthProvider,
	UserCredential,
	EmailAuthProvider,
	signInWithCustomToken
} from "@angular/fire/auth";
// import { FirebaseAuthentication, SignInResult } from "@capacitor-firebase/authentication";
import { Title } from "@angular/platform-browser";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { NavController, Platform } from "@ionic/angular";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import * as _ from "lodash-es";
import { Subscription, combineLatest, firstValueFrom, from, of } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { catchError, debounceTime, map, skipWhile, switchMap, take } from "rxjs/operators";
import { throwError, timer } from "rxjs";
import { IUser } from "src/app/shared/interfaces/user.interfaces";
import { PathApi } from "src/app/shared/paths/path-api";
import { environment } from "src/environments/environment";
import { EventUsersService, FirestoreService, MongodbService, PresenceService } from ".";
import { GetCurrentUser, LogInUser, LogOutUser } from "../actions/auth.actions";
import { InitAuth, InitClaims, InitEvents, InitUser, Loading } from "../actions/utility.actions";
import { TypeLogin } from "../enums/type-login";
import { IContainer, IEvent } from "../interfaces";
import { IContainersState } from "../interfaces/containers.interfaces";
import { getContainersStateAndUser } from "../selectors/auth.selectors";
import { getInitClaims, getInitContainers, getInitUser } from "../selectors/utility.selectors";
import { EventsService } from "./events.service";
import { StateManagerService } from "./state-manager.service";
import { TypeCaptchaLevel, TypeCaptchaMethod } from "../enums/type-register";
import { Functions, httpsCallableData } from "@angular/fire/functions";
import { Browser } from "@capacitor/browser";
import { DateTime } from "luxon";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { FavoritesService } from "./favorites.service";
import * as CryptoJS from "crypto-js";

@Injectable({
	providedIn: "root"
})
export class AuthService {
	Functions = inject(Functions);
	platform = inject(Platform);
	headers: HttpHeaders;
	deviceId: string = "";
	userId: string;

	userSubscription: Subscription;
	authSubscription: Subscription;
	ssoSubscription: Subscription;

	containerAndUserSubscription: Subscription;
	currentContainer: IContainer;

	eventsUserSubscription: Subscription;

	previousContainerAndUserState: {
		containersState: IContainersState;
		user: IUser;
	};
	previousUser: IUser;
	SFavorites = inject(FavoritesService);
	SPresenceService: any;
	SMongo = inject(MongodbService);
	STranslate = inject(TranslateService);
	route = inject(ActivatedRoute);
	snackbar = inject(MatSnackBar);

	constructor(
		private auth: Auth,
		private http: HttpClient,
		private navCtrl: NavController,
		private SFirestore: FirestoreService,
		private store: Store,
		private SEvents: EventsService,
		private SEventUsers: EventUsersService,
		private SStateManager: StateManagerService,
		private title: Title,
		private injector: Injector
	) {
		this.headers = new HttpHeaders();
		this.headers.append("content-type", "application/json");
		Device.getId()
			.then((device) => {
				this.deviceId = device.identifier;
				this.subscribeAuthState();
			})
			.catch(() => {
				this.subscribeAuthState();
			});
	}

	/**
	 * Auth subscription for basic actions log in/out, getting user data...
	 */
	subscribeAuthState() {
		/**
		 * Auth subscription for basic actions log in/out, getting user data...
		 */
		this.authSubscription = authState(this.auth).subscribe({
			next: (authState) => {
				if (authState) {
					// Logged in user
					Network.getStatus().then((status) => {
						if (status && status.connected) {
							this.claimsUser(authState.uid).subscribe({
								next: () => {
									this.store.dispatch(InitClaims({ payload: true }));
								},
								error: (error) => {
									this.store.dispatch(InitClaims({ payload: true }));
								}
							});
						} else {
							this.store.dispatch(InitClaims({ payload: true }));
						}
					});
					this.getUser(authState);
					this.store.dispatch(LogInUser({ payload: true }));
				} else {
					this.title.setTitle(environment.platform.appName);

					// Not logged in
					if (this.userSubscription && !this.userSubscription.closed) {
						this.userSubscription.unsubscribe();
					}
					this.store.dispatch(LogOutUser({ payload: false }));
					this.store.dispatch(InitUser({ payload: true }));
				}
				this.store.dispatch(InitAuth({ payload: true }));
			}
		});
		this.initListeningOnSsoSettings();
	}

	/**
	 * Initialize auth
	 */
	initAuth() {
		if (this.containerAndUserSubscription && !this.containerAndUserSubscription.closed) {
			this.containerAndUserSubscription.unsubscribe();
		}

		combineLatest([
			this.store.select(getInitContainers).pipe(skipWhile((init) => init === false)),
			this.store.select(getInitUser).pipe(skipWhile((init) => init === false))
		])
			.pipe(take(1))
			.subscribe(() => {
				this.containerAndUserSubscription = this.store
					.select(getContainersStateAndUser)
					.pipe(debounceTime(200))
					.subscribe((results) => {
						if (
							results &&
							results.user &&
							results.user.connectedPlatformToken &&
							results.user.connectedPlatformToken !== this.deviceId &&
							results.containersState.currentContainer.loginSettings.onlyOneAccountConnected
						) {
							this.logoutUser();
							return;
						}
						if (!_.isEqual(this.previousContainerAndUserState, results)) {
							const containersState = results.containersState;
							this.currentContainer =
								!environment.platform.containerId ||
								environment.platform.containerId === "defaultContainer"
									? containersState.defaultContainer
									: containersState.currentContainer;
							// const user = results.user;
							// this.SEvents.initEvents(
							// 	!environment.platform.containerId ||
							// 		environment.platform.containerId === "defaultContainer"
							// 		? "default"
							// 		: "client",
							// 	!environment.platform.containerId ||
							// 		environment.platform.containerId === "defaultContainer"
							// 		? containersState.defaultContainer
							// 		: containersState.currentContainer,
							// 	user
							// );
							this.store.dispatch(InitEvents({ payload: true }));
						}
						this.previousContainerAndUserState = results;
					});
			});
	}

	/**
	 * Getting user
	 */
	getUser(authState: User) {
		if (this.userSubscription && !this.userSubscription.closed) {
			this.userSubscription.unsubscribe();
		}
		this.store
			.select(getInitClaims)
			.pipe(
				skipWhile((init) => init === false),
				take(1)
			)
			.subscribe(() => {
				this.userId = authState.uid;
				this.userSubscription = this.SFirestore.snapshotChangesDocument(`users/${this.userId}`)
					.pipe(
						debounceTime(200),
						map((snapshot) => snapshot.data() as IUser),
						switchMap((user) => {
							return combineLatest([this.getUserByEmail(authState.email), of(user)]);
						})
					)
					.subscribe({
						next: ([userEmail, user]) => {
							if (user && !_.isEqual(this.previousUser, user)) {
								// Update first access on event-users document
								// firstValueFrom(this.SEventUsers.updateFirstAccessOnEventUsers(user.uid));
								// firstValueFrom(this.SEventUsers.updateLastAccessOnEventUsers(user.uid));
								this.store.dispatch(GetCurrentUser({ payload: user }));
								this.store.dispatch(InitUser({ payload: true }));
								this.previousUser = user;
							} else if (!user) {
								// If user don't exist with uid and email, logout
								if (!userEmail) {
									this.logoutUser();
								} else {
									// If user don't exist with same uid but exist with same email, actualize uid
									if (this.userSubscription && !this.userSubscription.closed) {
										this.userSubscription.unsubscribe();
									}
									this.actualizeWrongUidUser(authState.uid, authState.email).subscribe(() => {
										this.getUser(authState);
									});
								}
							}
						}
					});
			});
	}

	/**
	 * Update user data
	 * @param user
	 * @returns
	 */
	updateUser(user: IUser) {
		return this.SFirestore.updateDocument(`users/${user.uid}`, _.omit(user, ["uid", "type", "creationDate"]));
	}

	/**
	 * Takes email and password and attempts to log user in firebase
	 * @param {string} email
	 * @param {string} password
	 */
	loginUser(email: string, password: string) {
		return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
			switchMap((credential) => {
				return of(credential);
			}),
			catchError((error) => {
				// Gestion spécifique pour les erreurs iOS
				if (this.platform.is("ios") && error.code === "auth/network-request-failed") {
					console.warn("iOS network error during login, retrying...");
					// Retry logic spécifique pour iOS
					return timer(1000).pipe(
						switchMap(() => from(signInWithEmailAndPassword(this.auth, email, password))),
						catchError((retryError) => {
							console.error("Login failed on iOS after retry:", retryError);
							return throwError(() => retryError);
						})
					);
				}
				return throwError(() => error);
			})
		);
	}

	/**
	 * Actualize user and event user datas with good uids from auth account
	 * @param uidAuth
	 * @param email
	 * @returns
	 */
	actualizeWrongUidUser(uidAuth: string, email: string) {
		return httpsCallableData(this.Functions, "auth-actualizeWrongUidUser")({ uidAuth: uidAuth, email: email });
	}

	/**
	 * Attempts to log the user out from firebase
	 * @memberof AuthService
	 */
	async logoutUser() {
		this.SPresenceService = this.injector.get<PresenceService>(PresenceService);

		try {
			this.SPresenceService.setPresence("offline");
			this.SEvents.unsubscribeAll();
			this.SEventUsers.unsubscribeAll();
			this.SStateManager.unsubscribeAll();
			this.SStateManager.resetStateAll();
			this.previousUser = null;

			// FirebaseAuthentication.signOut();
			await signOut(this.auth);

			if (
				this.currentContainer &&
				(this.currentContainer.loginSettings.type === TypeLogin.WITHOUT_EMAIL_CONFIRM ||
					this.currentContainer.loginSettings.type === TypeLogin.WITH_EMAIL_CONFIRM)
			) {
				this.navCtrl.navigateRoot(["/login"]);
				// window.location.reload();
			} else if (
				this.currentContainer &&
				this.currentContainer.loginSettings.type === TypeLogin.PUBLIC_EVENT_HOME
			) {
				window.location.reload();
			}
		} catch (error) {
			// console.error("AuthService ~ logoutUser ~ error", error);
		}
	}

	/**
	 * Get user by email
	 * @param email
	 * @returns
	 */
	getUserByEmail(email: string) {
		return this.http
			.post(PathApi.authGetUserByEmail, { email: email, returnType: "partial" }, { headers: this.headers })
			.pipe(
				switchMap((results: any) => {
					if (results.code === 201 || results.code === 200) {
						return of(results.result);
					} else {
						return of(null);
					}
				})
			);
	}

	checkAuthAccountExist(email: string, uid: string, type: string) {
		return this.http.post(
			PathApi.checkAuthAccountExist,
			{ email: email, uid: uid, type: type },
			{ headers: this.headers }
		);
	}

	/**
	 *  Returns the user's custom authentication data
	 * @params userId
	 */
	claimsUser(userId: string) {
		return this.http.post(PathApi.authUpdateClaimsUser, { userId: userId }, { headers: this.headers }).pipe(
			switchMap(async () => {
				/**
				 * Here = less charging time but more consomation
				 */
				this.store.dispatch(InitClaims({ payload: true }));
				return await this.auth.currentUser.getIdToken(true);
			}),
			switchMap((token) => {
				return this.http.post(PathApi.authGetClaimsUser, { idToken: token }, { headers: this.headers });
			})
		);
	}

	/**
	 * Create user with auth
	 * @param user
	 * @param password
	 */
	createUserWithAuth(user: IUser, password: string) {
		const headers = new HttpHeaders();
		headers.append("Accept", "application/json");
		headers.append("Content-Type", "application/json");
		return firstValueFrom(
			this.http.post(
				PathApi.authCreateUser,
				{ password: password, user: user },
				{ headers: headers, observe: "body" }
			)
		);
	}

	/**
	 * Create user without auth
	 * @param user
	 */
	createUserWithoutAuth(user: IUser) {
		const id = !user.uid ? this.SFirestore.createId(`users`) : user.uid;
		user.uid = id;

		const headers = new HttpHeaders();
		headers.append("Accept", "application/json");
		headers.append("Content-Type", "application/json");
		return firstValueFrom(
			this.http
				.post(
					PathApi.authCreateUserWithoutAuth,
					{
						user: user
					},
					{
						headers: headers,
						observe: "body"
					}
				)
				.pipe(switchMap((data: any) => of(data.message)))
		);
	}

	/**
	 * Create only auth account
	 * @param user
	 * @param password
	 */
	createOnlyAuthAccount(user: IUser, password: string) {
		const headers = new HttpHeaders();
		headers.append("Accept", "application/json");
		headers.append("Content-Type", "application/json");
		return this.http.post(
			PathApi.authCreateOnlyAuthAccount,
			{
				password: password,
				user: user
			},
			{
				headers: headers,
				observe: "body"
			}
		);
	}

	/**
	 * Recover password
	 * @param email
	 * @returns
	 */
	recoveryPassword(email: string, containerId?: string, event?: IEvent) {
		return this.http.post(
			PathApi.sendToUserPasswordResetLink,
			{
				email: email,
				containerId: containerId || null,
				event: event || null
			},
			{
				headers: this.headers,
				observe: "body"
			}
		);
	}

	/**
	 * recoveryPasswordFromFirebase
	 * @param email
	 * @returns
	 */
	recoveryPasswordFromFirebase(email: string) {
		return sendPasswordResetEmail(this.auth, email);
	}

	/**
	 * Create and send code number
	 * @param userId
	 * @returns
	 */
	createAndSendCodeNumber(
		userId: string,
		containerId?: string
	): Observable<{ code: number; message: string; result: any; user: IUser }> {
		return this.http
			.post(
				PathApi.authCreateUser,
				{
					userId: userId,
					containerId: containerId || null
				},
				{
					headers: this.headers,
					observe: "body"
				}
			)
			.pipe(
				switchMap((result) => {
					return of(result as { code: number; message: string; result: any; user: IUser });
				})
			);
	}

	/**
	 * sendCodeNumber
	 * @param userId
	 * @returns
	 */
	sendCodeNumber(
		userId: string,
		deviceLanguage?: string,
		containerId?: string
	): Observable<{ code: number; message: string; result: any; user: IUser }> {
		return this.http
			.post(
				PathApi.authSendCodeNumber,
				{
					userId: userId,
					deviceLanguage: deviceLanguage,
					containerId: containerId || null
				},
				{
					headers: this.headers,
					observe: "body"
				}
			)
			.pipe(
				switchMap((result) => {
					return of(result as { code: number; message: string; result: any; user: IUser });
				})
			);
	}

	/**
	 * Verify code number for first access with email confirm
	 * @param codeNumber
	 * @param userId
	 * @returns
	 */
	verifyCodeNumber(codeNumber: string, userId: string) {
		return this.SFirestore.getDocumentObs(`users/${userId}`).pipe(
			switchMap((userDoc) => {
				const user = userDoc.data();
				if (user.codeNumber && user.codeNumber === codeNumber) {
					return of(true);
				} else {
					return of(false);
				}
			})
		);
	}

	/**
	 * verifyCaptcha
	 * @param event
	 * @param token
	 * @returns
	 */
	verifyCaptcha(event: IEvent, token: string) {
		return this.http.post(
			PathApi.authVerifyCaptcha,
			{
				token: token,
				captchaMethod: TypeCaptchaMethod.IMAGE,
				captchaLevel: TypeCaptchaLevel.HARD
			},
			{
				headers: this.headers,
				observe: "body"
			}
		);
	}

	/**
	 * getClientIP
	 * @returns
	 */
	getClientIP() {
		return this.http.get("https://api.ipify.org/?format=json");
	}

	/**
	 * Get user data
	 * @param userId
	 * @returns
	 */
	getUserData(userId: string) {
		return this.SFirestore.valueChangesDocument(`users/${userId}`);
	}

	/**
	 * Delete user and cascading on event user and auth account
	 * @param password
	 * @returns
	 */
	deleteUser(userId: string, userEmail: string, password: string) {
		return of(this.auth.currentUser).pipe(
			switchMap((user) => {
				const creds = EmailAuthProvider.credential(userEmail, password);
				return from(reauthenticateWithCredential(user, creds));
			}),
			switchMap(() => {
				return this.http.get(PathApi.authDeleteUser, {
					headers: this.headers,
					params: {
						userId: userId
					},
					observe: "body"
				});
			})
		);
	}

	deleteAuthAccount(email: string) {
		return this.http.post(PathApi.authDeleteAuthAccount, { email: email }, { headers: this.headers });
	}

	// Verify the password reset code is valid.
	verifyPasswordResetCode(oobCode: string) {
		return verifyPasswordResetCode(this.auth, oobCode);
	}

	// Confirm the new password reset.
	updateUserPassword(oobCode: string, newPassword: string) {
		return confirmPasswordReset(this.auth, oobCode, newPassword);
	}

	/*
	 *
	 * SSO FUNCTIONS
	 *
	 */

	initListeningOnSsoSettings() {
		if (this.platform.is("mobile") || this.platform.is("mobileweb")) {
			this.store.dispatch(Loading({ payload: true }));
			this.platform.ready().then(() => {
				this.ssoSubscription = this.SFirestore.valueChangesDocument(
					`sso-settings-token/${this.deviceId}`
				).subscribe({
					next: (ssoSettings) => {
						// Settings detected
						if (
							ssoSettings &&
							ssoSettings.provider &&
							ssoSettings.allowedDeviceId &&
							ssoSettings.allowedDeviceId === this.deviceId &&
							ssoSettings.expiresAt &&
							ssoSettings.expiresAt > Date.now() &&
							(ssoSettings.idToken || ssoSettings.accessToken)
						) {
							// All good, login with sso
							this.decryptTokenGCM(ssoSettings, environment?.platform?.ssoToken)
								.then((decryptedText) => {
									this.signInWithCustomToken(decryptedText, ssoSettings.provider)
										.then((userCredential) => {
											this.afterLoginSteps(userCredential);
											this.deleteSsoSettings(this.deviceId);
										})
										.catch(() => {
											this.deleteSsoSettings(this.deviceId);
										});
								})
								.catch(() => {
									this.deleteSsoSettings(this.deviceId);
								});
							// const key = CryptoJS.enc.Utf8.parse(environment?.platform?.ssoToken || "");
							// const iv = CryptoJS.enc.Base64.parse(ssoSettings.iv);
							// const decrypted = CryptoJS.AES.decrypt(ssoSettings.idToken, key, {
							// 	iv,
							// 	mode: CryptoJS.mode.CBC,
							// 	padding: CryptoJS.pad.Pkcs7
							// });
							// const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
							// this.signInWithCustomToken(decryptedText, ssoSettings.provider)
							// 	.then((userCredential) => {
							// 		this.afterLoginSteps(userCredential);
							// 		// Delete file
							// 		this.deleteSsoSettings(this.deviceId);
							// 	})
							// 	.catch((error) => {
							// 		this.deleteSsoSettings(this.deviceId);
							// 	});
						}
					}
				});
			});
		}
	}

	async decryptTokenGCM(encryptedData: any, secret: string): Promise<string> {
		const keyMaterial = new TextEncoder().encode(secret);
		const key = await crypto.subtle.importKey("raw", keyMaterial, { name: "AES-GCM" }, false, ["decrypt"]);

		const iv = Uint8Array.from(atob(encryptedData.iv), (c) => c.charCodeAt(0));
		const tag = Uint8Array.from(atob(encryptedData.tag), (c) => c.charCodeAt(0));
		const ciphertext = Uint8Array.from(atob(encryptedData.idToken), (c) => c.charCodeAt(0));

		// AES-GCM envoie le tag à la fin du ciphertext, donc on les concatène
		const encryptedWithTag = new Uint8Array(ciphertext.length + tag.length);
		encryptedWithTag.set(ciphertext);
		encryptedWithTag.set(tag, ciphertext.length);

		const decryptedBuffer = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, encryptedWithTag);

		return new TextDecoder().decode(decryptedBuffer);
	}

	signInWithCustomToken(credential: string, provider: string) {
		if (provider === "google" || provider === "microsoft" || provider === "oidc") {
			return signInWithCustomToken(this.auth, credential);
		}
		return null;
	}

	deleteSsoSettings(deviceId: string) {
		return this.SFirestore.deleteDocument(`sso-settings-token/${deviceId}`);
	}

	/**
	 * Login on app with oidc
	 * @returns
	 */
	async loginWithOIDC(containerId: string, oidcId: string) {
		const isNative = Capacitor.isNativePlatform();
		if (isNative || this.platform.is("mobileweb")) {
			let url = `https://b3app-auth.web.app?provider=oidc&deviceId=${this.deviceId}&oidcId=${oidcId}`;
			if (containerId) {
				url += `&containerId=${containerId}`;
			}
			Browser.open({
				url: url
			});
			return firstValueFrom(of(null));
		} else {
			// Web layer sign in with google
			const provider = new OAuthProvider(oidcId);
			provider.setCustomParameters({
				prompt: "login"
			});
			return signInWithPopup(this.auth, provider);
		}
	}

	/**
	 * Login on app with google
	 * @returns
	 */
	async loginWithGoogle(containerId: string) {
		const isNative = Capacitor.isNativePlatform();
		if (isNative || this.platform.is("mobileweb")) {
			let url = `https://b3app-auth.web.app?provider=google&deviceId=${this.deviceId}`;
			if (containerId) {
				url += `&containerId=${containerId}`;
			}
			Browser.open({
				url: url
			});

			return firstValueFrom(of(null));
		} else {
			// Web layer sign in with google
			return signInWithPopup(this.auth, new GoogleAuthProvider());
		}
	}

	/**
	 * Login on app with microsoft
	 * @returns
	 */
	async loginWithMicrosoft(containerId: string) {
		const isNative = Capacitor.isNativePlatform();
		if (isNative || this.platform.is("mobileweb")) {
			let url = `https://b3app-auth.web.app?provider=microsoft&deviceId=${this.deviceId}`;
			if (containerId) {
				url += `&containerId=${containerId}`;
			}
			Browser.open({
				url: url
			});
			return firstValueFrom(of(null));
		} else {
			// Web layer sign in with microsoft
			const provider = new OAuthProvider("microsoft.com");
			provider.setCustomParameters({ tenant: "common" });
			provider.addScope("email");
			provider.addScope("profile");
			provider.addScope("openid");
			return signInWithPopup(this.auth, provider);
		}
	}

	/**
	 * After login steps make actions
	 * @param userCreds
	 * @returns
	 */
	async afterLoginSteps(userCreds: UserCredential) {
		const results = await Promise.all([
			firstValueFrom(this.route.params.pipe(take(1))),
			firstValueFrom(this.getUserByEmail(userCreds.user.email))
		]);
		const eventId = results[0] && results[0].eventId ? results[0].eventId : "";
		const userData = results[1];
		if (userCreds && userCreds.user && userCreds.user.uid) {
			// If user data don't exist, delete auth account
			if (!userData) {
				await firstValueFrom(this.deleteAuthAccount(userCreds.user.email));
				this.store.dispatch(Loading({ payload: false }));
				this.snackbar.open(this.STranslate.instant("login.account-don't-exist"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
				return;
			}
			// If user exist update token and first access if necessary
			if (userData) {
				userData.connectedPlatformToken = this.deviceId ? this.deviceId : "";

				if (!userData.firstAccess) {
					// Update first access if not true
					userData.firstAccess = true;
				}
				this.updateUser(userData);
			}

			if (eventId && userData) {
				try {
					const currentEventUser = await firstValueFrom(
						this.SEventUsers.getSpecificEventUser(eventId, userData.uid)
					);
					if (currentEventUser) {
						await this.SFavorites.syncLocalFavorites(currentEventUser);

						if (!currentEventUser.firstAccess) {
							this.SEventUsers.updatePartOfEventUser(
								eventId,
								currentEventUser.moduleId,
								currentEventUser.uid,
								{
									firstAccess: true,
									firstAccessDate: DateTime.local().toISO()
								}
							);
						}
					}
				} catch (syncError) {
					this.snackbar.open(this.STranslate.instant("snackbar.error_syncing_favorites"), "", {
						duration: 3000,
						panelClass: "error-snackbar"
					});
				}
			}

			if (eventId) {
				this.store.dispatch(Loading({ payload: false }));
				// If event id in routing params, redirect to that event
				this.navCtrl.navigateRoot("/event/" + eventId);
			} else {
				this.store.dispatch(Loading({ payload: false }));
				// If no event id in routing params, redirect to list of events
				this.navCtrl.navigateRoot("/events-list/all");
			}

			if (eventId && userData) {
				// Update last access date property
				this.SEventUsers.updateLastAccessOfEventForEventUser(eventId, userData.uid);
			}
		} else {
			this.store.dispatch(Loading({ payload: false }));
			this.snackbar.open(this.STranslate.instant("login.invalid_pass"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}
}
